import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { Container } from 'components/Container/Container';
import { Icon } from 'components/Icon/Icon';
import { Input } from 'components/Input/Input';
import { Loader } from 'components/Loader/Loader';
import { PageTitle } from 'components/PageTitle/PageTitle';
import dayjs from 'dayjs';
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    ENTITIES,
    ENTITY_TYPES,
    LOCATIONS,
    RESERVATIONS,
    ADMIN_RESERVATION_CREATE,
    RESERVATION_CANCEL,
    RESERVATION_UPDATE_STATUS,
} from 'services/reservations';
import { USERS } from 'services/users/query';
import { useDebouncedCallback } from 'use-debounce';

interface Reservation {
    id: string;
    uuid: string;
    startDatetime: string;
    endDatetime: string;
    partySize: number;
    status: string;
    notes: string;
    internalNotes: string;
    customerName: string;
    isCancellable: boolean;
    entity: {
        id: string;
        name: string;
        entityType: { color: string };
        location: { name: string };
    };
    member?: {
        id: string;
        firstName: string;
        lastName: string;
        email: string;
        phone: string;
        allergies: string;
        dietRestrictions: string;
        likes: string;
        dislikes: string;
    };
    walkInName: string;
    walkInPhone: string;
}

interface Entity {
    id: string;
    name: string;
    capacity: number;
    entityType: { id: string; name: string; color: string };
    location: { id: string; name: string };
}

interface MemberOption {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
}

const HOURS = Array.from({ length: 14 }, (_, i) => i + 8); // 8 AM to 9 PM

export const ReservationCalendar: React.FC = () => {
    const { t } = useTranslation('translation');
    const [selectedDate, setSelectedDate] = useState(dayjs().startOf('week').format('YYYY-MM-DD'));
    const [filterEntityTypeId, setFilterEntityTypeId] = useState('');
    const [filterLocationId, setFilterLocationId] = useState('');
    const [filterMemberId, setFilterMemberId] = useState('');
    const [filterMemberSearch, setFilterMemberSearch] = useState('');
    const [filterMemberName, setFilterMemberName] = useState('');
    const [showFilterMemberDropdown, setShowFilterMemberDropdown] = useState(false);
    const [selectedEntities, setSelectedEntities] = useState<string[]>([]); // Multi-select entities
    const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
    const [showNewBooking, setShowNewBooking] = useState(false);
    const [newBookingEntity, setNewBookingEntity] = useState<Entity | null>(null);
    const [newBookingDate, setNewBookingDate] = useState(selectedDate);
    const [newBookingTime, setNewBookingTime] = useState({ start: '', end: '' });
    const [newBookingForm, setNewBookingForm] = useState({
        memberId: '',
        walkInName: '',
        walkInPhone: '',
        walkInEmail: '',
        partySize: 1,
        notes: '',
        internalNotes: '',
    });

    // Member search state
    const [memberSearch, setMemberSearch] = useState('');
    const [selectedMember, setSelectedMember] = useState<MemberOption | null>(null);
    const [showMemberDropdown, setShowMemberDropdown] = useState(false);

    // Week calculation
    const weekStart = dayjs(selectedDate).startOf('week');
    const weekDays = Array.from({ length: 7 }, (_, i) => weekStart.add(i, 'day'));

    const fromDate = weekStart.startOf('day').toISOString();
    const toDate = weekStart.endOf('week').toISOString();

    const { data: reservationsData, loading: loadingReservations, refetch } = useQuery(RESERVATIONS, {
        variables: { fromDate, toDate, memberId: filterMemberId || undefined },
    });

    const { data: entitiesData, loading: loadingEntities } = useQuery(ENTITIES, {
        variables: {
            activeOnly: true,
            entityTypeId: filterEntityTypeId || null,
            locationId: filterLocationId || null,
        },
    });

    const { data: entityTypesData } = useQuery(ENTITY_TYPES);
    const { data: locationsData } = useQuery(LOCATIONS);

    // Member search query
    const [searchMembers, { data: membersData, loading: searchingMembers }] = useLazyQuery(USERS);

    const debouncedSearch = useDebouncedCallback((query: string) => {
        if (query.length >= 2) {
            searchMembers({
                variables: {
                    search: query,
                    isMember: true,
                    status: ['ACTIVE'],
                    first: 10,
                },
            });
        }
    }, 300);

    const [searchFilterMembers, { data: filterMembersData, loading: searchingFilterMembers }] = useLazyQuery(USERS);

    const debouncedFilterSearch = useDebouncedCallback((query: string) => {
        if (query.length >= 2) {
            searchFilterMembers({
                variables: {
                    search: query,
                    isMember: true,
                    status: ['ACTIVE'],
                    first: 10,
                },
            });
        }
    }, 300);

    const handleFilterMemberSearch = (value: string) => {
        setFilterMemberSearch(value);
        setShowFilterMemberDropdown(true);
        debouncedFilterSearch(value);
    };

    const selectFilterMember = (member: MemberOption) => {
        setFilterMemberId(member.id);
        setFilterMemberName(`${member.firstName} ${member.lastName}`);
        setFilterMemberSearch('');
        setShowFilterMemberDropdown(false);
    };

    const clearFilterMember = () => {
        setFilterMemberId('');
        setFilterMemberName('');
        setFilterMemberSearch('');
        setShowFilterMemberDropdown(false);
    };

    const handleMemberSearch = (value: string) => {
        setMemberSearch(value);
        setShowMemberDropdown(true);
        debouncedSearch(value);
    };

    const selectMember = (member: MemberOption) => {
        setSelectedMember(member);
        setMemberSearch(`${member.firstName} ${member.lastName}`);
        setNewBookingForm({ ...newBookingForm, memberId: member.id, walkInName: '', walkInPhone: '', walkInEmail: '' });
        setShowMemberDropdown(false);
    };

    const clearMember = () => {
        setSelectedMember(null);
        setMemberSearch('');
        setNewBookingForm({ ...newBookingForm, memberId: '' });
    };

    const [createReservation, { loading: creating }] = useMutation(ADMIN_RESERVATION_CREATE, {
        onCompleted: () => {
            setShowNewBooking(false);
            resetBookingForm();
            refetch();
        },
    });

    const [cancelReservation, { loading: cancelling }] = useMutation(RESERVATION_CANCEL, {
        onCompleted: () => {
            setSelectedReservation(null);
            refetch();
        },
    });

    const [updateStatus] = useMutation(RESERVATION_UPDATE_STATUS, {
        onCompleted: () => refetch(),
    });

    // Extract data early for use in hooks
    const entities: Entity[] = entitiesData?.entities?.edges?.map((e: any) => e.node) || [];
    const reservations: Reservation[] = reservationsData?.reservations?.edges?.map((e: any) => e.node) || [];

    // Get available entities for selected time slot (must be before any early returns)
    const getAvailableEntitiesForTime = (date: string, startTime: string, endTime: string) => {
        if (!startTime || !endTime) return entities;

        const slotStart = dayjs(`${date} ${startTime}`);
        const slotEnd = dayjs(`${date} ${endTime}`);

        return entities.filter((entity) => {
            const hasConflict = reservations.some((r) => {
                if (r.entity.id !== entity.id) return false;
                if (!['pending', 'confirmed'].includes(r.status)) return false;

                const resStart = dayjs(r.startDatetime);
                const resEnd = dayjs(r.endDatetime);

                return resStart.isBefore(slotEnd) && resEnd.isAfter(slotStart);
            });

            return !hasConflict;
        });
    };

    const availableEntitiesForBooking = useMemo(
        () => getAvailableEntitiesForTime(newBookingDate, newBookingTime.start, newBookingTime.end),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [newBookingDate, newBookingTime.start, newBookingTime.end, entities, reservations]
    );

    const resetBookingForm = () => {
        setNewBookingForm({
            memberId: '',
            walkInName: '',
            walkInPhone: '',
            walkInEmail: '',
            partySize: 1,
            notes: '',
            internalNotes: '',
        });
        setSelectedMember(null);
        setMemberSearch('');
        setNewBookingEntity(null);
    };

    const handleCreateReservation = () => {
        if (!newBookingEntity || !newBookingTime.start || !newBookingTime.end) return;

        const startDatetime = dayjs(`${newBookingDate} ${newBookingTime.start}`).toISOString();
        const endDatetime = dayjs(`${newBookingDate} ${newBookingTime.end}`).toISOString();

        createReservation({
            variables: {
                input: {
                    entityId: newBookingEntity.id,
                    startDatetime,
                    endDatetime,
                    memberId: newBookingForm.memberId || null,
                    walkInName: newBookingForm.walkInName || null,
                    walkInPhone: newBookingForm.walkInPhone || null,
                    walkInEmail: newBookingForm.walkInEmail || null,
                    partySize: newBookingForm.partySize,
                    notes: newBookingForm.notes,
                    internalNotes: newBookingForm.internalNotes,
                },
            },
        });
    };

    const handleCancelReservation = (id: string) => {
        if (window.confirm(t('reservations.confirmCancelReservation'))) {
            cancelReservation({ variables: { id } });
        }
    };

    const handleStatusUpdate = (id: string, status: string) => {
        updateStatus({ variables: { id, status } });
    };

    if (loadingEntities || loadingReservations) return <Loader />;

    const entityTypes = entityTypesData?.entityTypes?.edges?.map((e: any) => e.node) || [];
    const locations = locationsData?.locations?.edges?.map((e: any) => e.node) || [];

    const getReservationPosition = (reservation: Reservation) => {
        const start = dayjs(reservation.startDatetime);
        const end = dayjs(reservation.endDatetime);
        const startHour = start.hour() + start.minute() / 60;
        const duration = end.diff(start, 'minute') / 60;
        const top = (startHour - 8) * 60;
        const height = duration * 60;
        return { top, height };
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'confirmed':
                return 'bg-green-100 border-green-500 text-green-800';
            case 'pending':
                return 'bg-yellow-100 border-yellow-500 text-yellow-800';
            case 'cancelled':
                return 'bg-red-100 border-red-500 text-red-800';
            default:
                return 'bg-gray-100 border-gray-500 text-gray-800';
        }
    };

    const toggleEntitySelection = (entityId: string) => {
        setSelectedEntities((prev) =>
            prev.includes(entityId) ? prev.filter((id) => id !== entityId) : [...prev, entityId]
        );
    };

    const openNewBookingModal = (entity?: Entity, date?: string, time?: { start: string; end: string }) => {
        setNewBookingEntity(entity || null);
        setNewBookingDate(date || dayjs().format('YYYY-MM-DD'));
        setNewBookingTime(time || { start: '12:00', end: '13:00' });
        setShowNewBooking(true);
    };

    return (
        <Container>
            {/* Header */}
            <div className="flex flex-col gap-4 mb-6">
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                    <PageTitle>{t('reservations.calendar')}</PageTitle>
                    <Button onClick={() => openNewBookingModal()}>{t('reservations.newReservation')}</Button>
                </div>

                {/* Week Navigation & Filters */}
                <div className="flex flex-wrap gap-3 items-center">
                    <div className="flex items-center gap-1 bg-white border border-gray-300 rounded-lg">
                        <button
                            type="button"
                            onClick={() => setSelectedDate(weekStart.subtract(1, 'week').format('YYYY-MM-DD'))}
                            className="p-2 hover:bg-gray-100 rounded-l-lg"
                        >
                            {t('reservations.prevWeek')}
                        </button>
                        <span className="px-3 py-2 font-medium text-sm">
                            {weekStart.format('MMM D')} - {weekStart.add(6, 'day').format('MMM D, YYYY')}
                        </span>
                        <button
                            type="button"
                            onClick={() => setSelectedDate(weekStart.add(1, 'week').format('YYYY-MM-DD'))}
                            className="p-2 hover:bg-gray-100 rounded-r-lg"
                        >
                            {t('reservations.nextWeek')}
                        </button>
                    </div>

                    <Button variant="outline" onClick={() => setSelectedDate(dayjs().startOf('week').format('YYYY-MM-DD'))}>
                        {t('reservations.thisWeek')}
                    </Button>

                    {/* Entity Type Filter */}
                    <div className="relative">
                        <div className="flex items-center gap-2 border border-dashed border-zinc-300 hover:border-rose-700 px-3 h-9 cursor-pointer bg-white">
                            <Icon name="carret-down" className="text-zinc-500 text-sm" />
                            <select
                                className="appearance-none bg-transparent text-sm cursor-pointer outline-none pr-2"
                                value={filterEntityTypeId}
                                onChange={(e) => setFilterEntityTypeId(e.target.value)}
                            >
                                <option value="">{t('reservations.allTypes')}</option>
                                {entityTypes.map((type: any) => (
                                    <option key={type.id} value={type.id}>
                                        {type.name}
                                    </option>
                                ))}
                            </select>
                            {filterEntityTypeId && (
                                <>
                                    <div className="w-px h-4 bg-zinc-200" />
                                    <div className="flex items-center gap-1 px-2 py-1 bg-rose-50 text-zinc-600 text-sm">
                                        {entityTypes.find((t: any) => t.id === filterEntityTypeId)?.name}
                                        <Icon
                                            name="close"
                                            className="cursor-pointer text-xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFilterEntityTypeId('');
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Location Filter */}
                    <div className="relative">
                        <div className="flex items-center gap-2 border border-dashed border-zinc-300 hover:border-rose-700 px-3 h-9 cursor-pointer bg-white">
                            <Icon name="carret-down" className="text-zinc-500 text-sm" />
                            <select
                                className="appearance-none bg-transparent text-sm cursor-pointer outline-none pr-2"
                                value={filterLocationId}
                                onChange={(e) => setFilterLocationId(e.target.value)}
                            >
                                <option value="">{t('reservations.allLocations')}</option>
                                {locations.map((loc: any) => (
                                    <option key={loc.id} value={loc.id}>
                                        {loc.name}
                                    </option>
                                ))}
                            </select>
                            {filterLocationId && (
                                <>
                                    <div className="w-px h-4 bg-zinc-200" />
                                    <div className="flex items-center gap-1 px-2 py-1 bg-rose-50 text-zinc-600 text-sm">
                                        {locations.find((l: any) => l.id === filterLocationId)?.name}
                                        <Icon
                                            name="close"
                                            className="cursor-pointer text-xs"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setFilterLocationId('');
                                            }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Member Filter */}
                    <div className="relative">
                        {filterMemberId ? (
                            <div className="flex items-center gap-2 border border-dashed border-zinc-300 hover:border-rose-700 px-3 h-9 bg-white">
                                <Icon name="user" className="text-zinc-500 text-sm" />
                                <span className="text-sm">{filterMemberName}</span>
                                <div className="w-px h-4 bg-zinc-200" />
                                <div className="flex items-center gap-1 px-2 py-1 bg-rose-50 text-zinc-600 text-sm">
                                    {filterMemberName}
                                    <Icon
                                        name="close"
                                        className="cursor-pointer text-xs"
                                        onClick={clearFilterMember}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 border border-dashed border-zinc-300 hover:border-rose-700 px-3 h-9 bg-white">
                                <Icon name="carret-down" className="text-zinc-500 text-sm" />
                                <input
                                    type="text"
                                    placeholder={t('reservations.filterByMember')}
                                    value={filterMemberSearch}
                                    onChange={(e) => handleFilterMemberSearch(e.target.value)}
                                    onFocus={() => setShowFilterMemberDropdown(true)}
                                    className="appearance-none bg-transparent text-sm outline-none w-36"
                                />
                            </div>
                        )}
                        {showFilterMemberDropdown && filterMemberSearch.length >= 2 && (
                            <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-48 overflow-y-auto">
                                {searchingFilterMembers && (
                                    <div className="px-3 py-2 text-sm text-gray-500">{t('reservations.searching')}</div>
                                )}
                                {!searchingFilterMembers && filterMembersData?.users?.edges?.length > 0 && (
                                    filterMembersData.users.edges.map((edge: any) => {
                                        const user = edge.node;
                                        return (
                                            <button
                                                key={user.id}
                                                type="button"
                                                onClick={() => selectFilterMember(user)}
                                                className="w-full text-left px-3 py-2 hover:bg-gray-100 text-sm"
                                            >
                                                {user.firstName} {user.lastName}
                                                <span className="text-gray-400 ml-2">{user.email}</span>
                                            </button>
                                        );
                                    })
                                )}
                                {!searchingFilterMembers && !filterMembersData?.users?.edges?.length && (
                                    <div className="px-3 py-2 text-sm text-gray-500">{t('reservations.noMembersFound')}</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Entity Selection (Tables/Spaces) */}
                <div className="flex items-center gap-2 border border-dashed border-zinc-300 hover:border-rose-700 px-3 h-9 bg-white">
                    <Icon name="carret-down" className="text-zinc-500 text-sm" />
                    <span className="text-sm">{t('reservations.tablesSpaces')}</span>
                    {selectedEntities.length === 0 ? (
                        <span className="text-zinc-400 text-sm">{t('reservations.all')} ({entities.length})</span>
                    ) : (
                        <>
                            <div className="w-px h-4 bg-zinc-200" />
                            <div className="flex items-center gap-1 flex-wrap">
                                {selectedEntities.slice(0, 3).map((entityId) => {
                                    const entity = entities.find((e) => e.id === entityId);
                                    if (!entity) return null;
                                    return (
                                        <div
                                            key={entity.id}
                                            className="flex items-center gap-1 px-2 py-0.5 bg-rose-50 text-zinc-600 text-sm"
                                            style={{ borderLeft: `3px solid ${entity.entityType.color}` }}
                                        >
                                            {entity.name}
                                            <Icon
                                                name="close"
                                                className="cursor-pointer text-xs"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    toggleEntitySelection(entity.id);
                                                }}
                                            />
                                        </div>
                                    );
                                })}
                                {selectedEntities.length > 3 && (
                                    <span className="text-zinc-500 text-sm">+{selectedEntities.length - 3} {t('reservations.more')}</span>
                                )}
                            </div>
                            <button
                                type="button"
                                className="ml-2 text-xs text-zinc-500 hover:text-rose-600"
                                onClick={() => setSelectedEntities([])}
                            >
                                {t('reservations.clear')}
                            </button>
                        </>
                    )}
                </div>

                {/* Entity Chips Row */}
                <div className="flex flex-wrap gap-2">
                    {entities.map((entity) => {
                        const isSelected = selectedEntities.includes(entity.id);
                        const noFilterActive = selectedEntities.length === 0;

                        const getChipClassName = () => {
                            if (isSelected) return 'bg-rose-50 text-zinc-700';
                            if (noFilterActive) return 'bg-white border border-dashed border-zinc-300 text-zinc-600 hover:border-rose-300 hover:bg-rose-50';
                            return 'bg-zinc-100 text-zinc-400 border border-zinc-200';
                        };

                        return (
                            <button
                                key={entity.id}
                                type="button"
                                onClick={() => toggleEntitySelection(entity.id)}
                                className={`flex items-center gap-1 px-2 py-1 text-sm transition-colors ${getChipClassName()}`}
                                style={{ borderLeft: `3px solid ${entity.entityType.color}` }}
                            >
                                {entity.name}
                                <span className="text-xs opacity-60">({entity.capacity})</span>
                                {isSelected && (
                                    <Icon
                                        name="close"
                                        className="ml-1 text-xs cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleEntitySelection(entity.id);
                                        }}
                                    />
                                )}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Weekly Calendar Grid */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {/* Week Header */}
                <div className="flex border-b">
                    <div className="w-20 flex-shrink-0 bg-gray-50 p-2 text-center text-sm font-medium text-gray-600">
                        {t('reservations.time')}
                    </div>
                    {weekDays.map((day) => (
                        <div
                            key={day.format('YYYY-MM-DD')}
                            className={`flex-1 p-2 text-center border-l ${
                                day.isSame(dayjs(), 'day') ? 'bg-rose-50' : 'bg-gray-50'
                            }`}
                        >
                            <div className={`text-sm font-medium ${day.isSame(dayjs(), 'day') ? 'text-rose-600' : 'text-gray-700'}`}>
                                {day.format('ddd')}
                            </div>
                            <div className={`text-lg ${day.isSame(dayjs(), 'day') ? 'text-rose-600 font-bold' : 'text-gray-900'}`}>
                                {day.format('D')}
                            </div>
                        </div>
                    ))}
                </div>

                {/* Time Grid */}
                <div className="flex">
                    {/* Time Column */}
                    <div className="w-20 flex-shrink-0 border-r bg-gray-50">
                        {HOURS.map((hour) => (
                            <div key={hour} className="h-[60px] text-xs text-gray-500 text-right pr-2 pt-1 border-b">
                                {hour}:00
                            </div>
                        ))}
                    </div>

                    {/* Day Columns */}
                    <div className="flex flex-1 overflow-x-auto">
                        {weekDays.map((day) => (
                            <div key={day.format('YYYY-MM-DD')} className="flex-1 min-w-[120px] border-l relative">
                                <div style={{ height: HOURS.length * 60 }} className="relative">
                                    {/* Hour lines */}
                                    {HOURS.map((hour) => (
                                        <div
                                            key={hour}
                                            className="absolute w-full border-b border-gray-100"
                                            style={{ top: (hour - 8) * 60 }}
                                        />
                                    ))}

                                    {/* Reservations for this day (filtered by selected entities) */}
                                    {reservations
                                        .filter(
                                            (r) =>
                                                dayjs(r.startDatetime).isSame(day, 'day') &&
                                                ['pending', 'confirmed'].includes(r.status) &&
                                                (selectedEntities.length === 0 || selectedEntities.includes(r.entity.id))
                                        )
                                        .map((reservation) => {
                                            const { top, height } = getReservationPosition(reservation);
                                            return (
                                                <div
                                                    key={reservation.id}
                                                    className={`absolute left-1 right-1 rounded cursor-pointer border-l-4 px-1 overflow-hidden text-xs ${getStatusColor(reservation.status)}`}
                                                    style={{ top, height: Math.max(height, 24), zIndex: 10 }}
                                                    onClick={() => setSelectedReservation(reservation)}
                                                >
                                                    <div className="font-medium truncate">{reservation.customerName}</div>
                                                    <div className="truncate opacity-75">{reservation.entity.name}</div>
                                                    <div className="opacity-60">
                                                        {dayjs(reservation.startDatetime).format('HH:mm')}
                                                    </div>
                                                </div>
                                            );
                                        })}

                                    {/* Click to add */}
                                    <div
                                        className="absolute inset-0 cursor-pointer"
                                        onClick={(e) => {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            const y = e.clientY - rect.top;
                                            const hour = Math.floor(y / 60) + 8;
                                            const minutes = Math.floor((y % 60) / 30) * 30;
                                            const startTime = `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                                            const endHour = hour + 1;
                                            const endTime = `${endHour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
                                            openNewBookingModal(undefined, day.format('YYYY-MM-DD'), { start: startTime, end: endTime });
                                        }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Reservation Detail Modal */}
            {selectedReservation && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg">
                        <h3 className="text-lg font-semibold mb-4">{t('reservations.reservationDetails')}</h3>
                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">{t('reservations.customer')}</span>
                                <span className="font-medium">{selectedReservation.customerName}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">{t('reservations.table')}</span>
                                <span>{selectedReservation.entity.name}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">{t('reservations.date')}</span>
                                <span>{dayjs(selectedReservation.startDatetime).format('ddd, MMM D, YYYY')}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">{t('reservations.time')}</span>
                                <span>
                                    {dayjs(selectedReservation.startDatetime).format('HH:mm')} -{' '}
                                    {dayjs(selectedReservation.endDatetime).format('HH:mm')}
                                </span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">{t('reservations.partySize')}</span>
                                <span>{selectedReservation.partySize}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-600">{t('reservations.status')}</span>
                                <Badge variant={selectedReservation.status === 'confirmed' ? 'default' : 'secondary'}>
                                    {selectedReservation.status}
                                </Badge>
                            </div>
                            {selectedReservation.notes && (
                                <div>
                                    <span className="text-gray-600">{t('reservations.notes')}</span>
                                    <p className="mt-1 text-sm">{selectedReservation.notes}</p>
                                </div>
                            )}
                            {selectedReservation.member && (
                                <div className="border-t pt-3 mt-3">
                                    <h4 className="font-medium mb-2">{t('reservations.memberPreferences')}</h4>
                                    {selectedReservation.member.allergies && (
                                        <p className="text-sm text-red-600">
                                            <strong>{t('reservations.memberAllergies')}</strong> {selectedReservation.member.allergies}
                                        </p>
                                    )}
                                    {selectedReservation.member.dietRestrictions && (
                                        <p className="text-sm text-orange-600">
                                            <strong>{t('reservations.memberDietRestrictions')}</strong> {selectedReservation.member.dietRestrictions}
                                        </p>
                                    )}
                                    {selectedReservation.member.likes && (
                                        <p className="text-sm text-green-600">
                                            <strong>{t('reservations.memberLikes')}</strong> {selectedReservation.member.likes}
                                        </p>
                                    )}
                                    {selectedReservation.member.dislikes && (
                                        <p className="text-sm text-gray-600">
                                            <strong>{t('reservations.memberDislikes')}</strong> {selectedReservation.member.dislikes}
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="flex gap-2 mt-6 flex-wrap">
                            {selectedReservation.status === 'pending' && (
                                <Button onClick={() => handleStatusUpdate(selectedReservation.id, 'confirmed')}>
                                    {t('reservations.confirmAction')}
                                </Button>
                            )}
                            {selectedReservation.status === 'confirmed' && (
                                <>
                                    <Button onClick={() => handleStatusUpdate(selectedReservation.id, 'completed')}>
                                        Mark Completed
                                    </Button>
                                    <Button variant="outline" onClick={() => handleStatusUpdate(selectedReservation.id, 'no_show')}>
                                        No Show
                                    </Button>
                                </>
                            )}
                            {['pending', 'confirmed'].includes(selectedReservation.status) && (
                                <Button
                                    variant="destructive"
                                    onClick={() => handleCancelReservation(selectedReservation.id)}
                                    disabled={cancelling}
                                >
                                    {t('reservations.cancelReservation')}
                                </Button>
                            )}
                            <Button variant="outline" onClick={() => setSelectedReservation(null)}>
                                {t('reservations.close')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* New Booking Modal */}
            {showNewBooking && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
                        <h3 className="text-lg font-semibold mb-4">{t('reservations.newBooking')}</h3>
                        <div className="space-y-4">
                            {/* Date & Time Selection */}
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('reservations.selectDate')}</label>
                                    <Input
                                        type="date"
                                        value={newBookingDate}
                                        onChange={(e) => {
                                            setNewBookingDate(e.target.value);
                                            setNewBookingEntity(null); // Reset entity when date changes
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('reservations.startTime')}</label>
                                    <Input
                                        type="time"
                                        value={newBookingTime.start}
                                        onChange={(e) => {
                                            setNewBookingTime({ ...newBookingTime, start: e.target.value });
                                            setNewBookingEntity(null); // Reset entity when time changes
                                        }}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">{t('reservations.endTime')}</label>
                                    <Input
                                        type="time"
                                        value={newBookingTime.end}
                                        onChange={(e) => {
                                            setNewBookingTime({ ...newBookingTime, end: e.target.value });
                                            setNewBookingEntity(null); // Reset entity when time changes
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Available Tables/Spaces */}
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {t('reservations.selectEntity')}
                                    {newBookingTime.start && newBookingTime.end && (
                                        <span className="text-gray-500 font-normal ml-1">
                                            ({availableEntitiesForBooking.length} of {entities.length} {t('reservations.availableEntities')})
                                        </span>
                                    )}
                                </label>
                                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                                    {availableEntitiesForBooking.length === 0 ? (
                                        <div className="col-span-2 text-center py-4 text-gray-500">
                                            {t('reservations.noAvailableEntities')}
                                        </div>
                                    ) : (
                                        availableEntitiesForBooking.map((entity) => (
                                            <button
                                                key={entity.id}
                                                type="button"
                                                onClick={() => setNewBookingEntity(entity)}
                                                className={`p-2 rounded border text-left transition-colors ${
                                                    newBookingEntity?.id === entity.id
                                                        ? 'border-rose-500 bg-rose-50'
                                                        : 'border-gray-200 hover:border-rose-300 hover:bg-gray-50'
                                                }`}
                                            >
                                                <div className="font-medium text-sm">{entity.name}</div>
                                                <div className="text-xs text-gray-500">
                                                    {entity.location.name} • Capacity: {entity.capacity}
                                                </div>
                                            </button>
                                        ))
                                    )}
                                </div>
                            </div>

                            {/* Member search */}
                            <div>
                                <label className="block text-sm font-medium mb-1">{t('reservations.searchMember')}</label>
                                <div className="relative">
                                    <Input
                                        value={memberSearch}
                                        onChange={(e) => handleMemberSearch(e.target.value)}
                                        onFocus={() => memberSearch.length >= 2 && setShowMemberDropdown(true)}
                                        placeholder={t('reservations.searchMember')}
                                        disabled={!!selectedMember}
                                    />
                                    {selectedMember && (
                                        <button
                                            type="button"
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            onClick={clearMember}
                                        >
                                            ✕
                                        </button>
                                    )}
                                    {showMemberDropdown && !selectedMember && membersData?.users?.edges?.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                                            {membersData.users.edges.map((edge: any) => (
                                                <div
                                                    key={edge.node.id}
                                                    className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                                                    onClick={() => selectMember(edge.node)}
                                                >
                                                    <div className="font-medium">
                                                        {edge.node.firstName} {edge.node.lastName}
                                                    </div>
                                                    <div className="text-sm text-gray-500">
                                                        {edge.node.email} {edge.node.phoneNumber && `• ${edge.node.phoneNumber}`}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {showMemberDropdown && searchingMembers && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg p-3 text-center text-gray-500">
                                            {t('reservations.searching')}
                                        </div>
                                    )}
                                </div>
                                {selectedMember && (
                                    <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm">
                                        <strong>{t('reservations.selected')}</strong> {selectedMember.firstName} {selectedMember.lastName}
                                        {selectedMember.phoneNumber && <span> • {selectedMember.phoneNumber}</span>}
                                    </div>
                                )}
                            </div>

                            {/* Walk-in fields */}
                            {!selectedMember && (
                                <>
                                    <div className="text-sm text-gray-500 text-center">— or enter walk-in details —</div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">{t('reservations.guestName')}</label>
                                        <Input
                                            value={newBookingForm.walkInName}
                                            onChange={(e) => setNewBookingForm({ ...newBookingForm, walkInName: e.target.value })}
                                            placeholder={t('reservations.guestName')}
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">{t('reservations.guestPhone')}</label>
                                            <Input
                                                value={newBookingForm.walkInPhone}
                                                onChange={(e) => setNewBookingForm({ ...newBookingForm, walkInPhone: e.target.value })}
                                                placeholder={t('reservations.guestPhone')}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">{t('reservations.guestEmail')}</label>
                                            <Input
                                                value={newBookingForm.walkInEmail}
                                                onChange={(e) => setNewBookingForm({ ...newBookingForm, walkInEmail: e.target.value })}
                                                placeholder={t('reservations.guestEmail')}
                                            />
                                        </div>
                                    </div>
                                </>
                            )}

                            <div>
                                <label className="block text-sm font-medium mb-1">{t('reservations.partySize')}</label>
                                <Input
                                    type="number"
                                    value={newBookingForm.partySize}
                                    onChange={(e) =>
                                        setNewBookingForm({
                                            ...newBookingForm,
                                            partySize: parseInt(e.target.value) || 1,
                                        })
                                    }
                                    min={1}
                                    max={newBookingEntity?.capacity || 100}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">{t('reservations.notes')}</label>
                                <textarea
                                    className="w-full border rounded px-3 py-2"
                                    value={newBookingForm.notes}
                                    onChange={(e) => setNewBookingForm({ ...newBookingForm, notes: e.target.value })}
                                    placeholder={t('reservations.notes')}
                                    rows={2}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-1">{t('reservations.internalNotes')}</label>
                                <textarea
                                    className="w-full border rounded px-3 py-2"
                                    value={newBookingForm.internalNotes}
                                    onChange={(e) => setNewBookingForm({ ...newBookingForm, internalNotes: e.target.value })}
                                    placeholder={t('reservations.internalNotes')}
                                    rows={2}
                                />
                            </div>
                        </div>

                        <div className="flex gap-2 mt-6">
                            <Button
                                onClick={handleCreateReservation}
                                disabled={creating || !newBookingEntity || (!selectedMember && !newBookingForm.walkInName)}
                            >
                                {creating ? 'Creating...' : t('reservations.createReservation')}
                            </Button>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowNewBooking(false);
                                    resetBookingForm();
                                }}
                            >
                                {t('reservations.cancel')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
};
